import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddressAutocompleteInput from 'components/common/AddressAutocompleteInput';
import axiosInstance from 'helpers/api';
import './HomeValuation.css';

/**
 * KOPS-518: "What's My Home Worth" — Uber-simple seller flow
 *
 * 1. Seller enters address (ONE field, nothing else)
 * 2. AI runs ML valuation in 5-6 seconds
 * 3. Redirects to /cma-report/{reportId} where seller chats with AI
 *
 * No forms. No beds/baths/sqft. We already have MLS data.
 * Like ordering an Uber — address in, value out.
 */
const HomeValuation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [address, setAddress] = useState('');
  const [fullAddress, setFullAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);

  // If address is in URL params, auto-run
  useEffect(() => {
    const urlAddress = searchParams.get('address');
    if (urlAddress && !loading) {
      setAddress(urlAddress);
      setFullAddress(urlAddress);
      handleGetValuation(urlAddress);
    }
  }, []);

  const loadingSteps = [
    'Finding your property in MLS...',
    'Analyzing 1.6M+ closed transactions...',
    'Running AI valuation model...',
    'Finding comparable sales nearby...',
    'Preparing your personalized report...'
  ];

  // Cycle through loading messages
  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setLoadingStep(prev => (prev + 1) % loadingSteps.length);
    }, 1200);
    return () => clearInterval(interval);
  }, [loading]);

  const handleAddressSelect = (suggestion: any) => {
    setAddress(suggestion.address);
    setFullAddress(suggestion.address);
  };

  const handleGetValuation = async (addressToUse?: string) => {
    const addr = addressToUse || fullAddress || address;
    if (!addr.trim()) return;

    setLoading(true);
    setError('');
    setLoadingStep(0);

    try {
      // Call the public valuation endpoint — no auth, no lead required
      const response = await axiosInstance.post('/sellers/public-valuation', {
        address: addr
      });

      if (response.data.success && response.data.reportId) {
        // Store valuation summary for the CMA page to use
        sessionStorage.setItem('publicValuation', JSON.stringify({
          leadId: response.data.leadId,
          reportId: response.data.reportId,
          valuationSummary: response.data.valuationSummary,
          address: addr,
          source: 'whats_my_home_worth'
        }));

        // Redirect to the existing CMA report page — seller chats with AI there
        navigate(`/cma-report/${response.data.reportId}`);
      } else {
        setError(response.data.error || 'Could not generate valuation. Please check the address and try again.');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Valuation error:', err);
      const message = err.response?.data?.error || 'Something went wrong. Please try again.';
      setError(message);
      setLoading(false);
    }
  };

  // Loading screen — the "magic is happening" moment
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0F1E35 0%, #1E3557 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontFamily: "'DM Sans', -apple-system, sans-serif"
      }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '0 20px' }}>
          <Spinner animation="border" variant="warning" style={{ width: '60px', height: '60px', marginBottom: '24px' }} />
          <h2 style={{ fontSize: '28px', marginBottom: '12px' }}>Analyzing Your Home</h2>
          <p style={{ color: '#C9952A', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
            {loadingSteps[loadingStep]}
          </p>
          <p style={{ color: '#8FA3BF', fontSize: '14px' }}>
            {address}
          </p>
          <div style={{ marginTop: '32px' }}>
            <div style={{
              background: 'rgba(201,149,42,0.2)',
              borderRadius: '8px',
              padding: '16px',
              fontSize: '13px',
              color: '#8FA3BF'
            }}>
              Our AI analyzes 197 zip-code models trained on 1.6M+ closed transactions to give you the most accurate valuation possible.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main screen — just an address bar
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F1E35 0%, #1E3557 100%)',
      fontFamily: "'DM Sans', -apple-system, sans-serif"
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Minimal nav */}
      <nav style={{ padding: '16px 0', borderBottom: '1px solid rgba(201,149,42,0.2)' }}>
        <Container className="d-flex justify-content-between align-items-center">
          <a href="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: 'white' }}>
              chat<span style={{ color: '#E8B44A' }}>.realestate</span>
            </span>
          </a>
          <div className="d-flex gap-3">
            <a href="/chat" style={{ color: '#8FA3BF', textDecoration: 'none', fontSize: '14px' }}>Buy a Home</a>
            <a href="/home-value" style={{ color: '#E8B44A', textDecoration: 'none', fontSize: '14px', fontWeight: 700 }}>Sell My Home</a>
          </div>
        </Container>
      </nav>

      {/* Hero — just the address input */}
      <Container>
        <Row className="justify-content-center" style={{ paddingTop: '12vh' }}>
          <Col lg={8} xl={7}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h1 style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: '56px',
                letterSpacing: '2px',
                color: 'white',
                lineHeight: 1.1,
                marginBottom: '16px'
              }}>
                What's Your Home <span style={{ color: '#E8B44A' }}>Worth?</span>
              </h1>
              <p style={{ fontSize: '20px', color: '#8FA3BF', maxWidth: '500px', margin: '0 auto' }}>
                Enter your address. AI does the rest.
              </p>
            </div>

            {/* THE address input — the only thing the seller interacts with */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '8px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              <div style={{ flex: 1 }}>
                <AddressAutocompleteInput
                  value={address}
                  onChange={(val) => setAddress(val)}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Enter your home address..."
                  required
                />
              </div>
              <Button
                onClick={() => handleGetValuation()}
                disabled={!address.trim()}
                style={{
                  background: '#C9952A',
                  border: 'none',
                  color: '#0F1E35',
                  fontWeight: 700,
                  fontSize: '16px',
                  padding: '14px 32px',
                  borderRadius: '12px',
                  whiteSpace: 'nowrap'
                }}
              >
                Get My Value
              </Button>
            </div>

            {error && (
              <div style={{
                marginTop: '16px',
                padding: '12px 16px',
                background: 'rgba(220,53,69,0.15)',
                border: '1px solid rgba(220,53,69,0.3)',
                borderRadius: '8px',
                color: '#ff6b7a',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}

            {/* Trust signals */}
            <div className="d-flex justify-content-center gap-4 flex-wrap" style={{ marginTop: '32px' }}>
              {[
                { label: '1.6M+ Transactions', sub: 'Trained on' },
                { label: '197 Zip Models', sub: 'AI powered' },
                { label: '90% Accuracy', sub: 'Prediction' },
                { label: '5 Seconds', sub: 'Results in' }
              ].map((badge, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ color: '#E8B44A', fontSize: '18px', fontWeight: 700 }}>{badge.label}</div>
                  <div style={{ color: '#8FA3BF', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>{badge.sub}</div>
                </div>
              ))}
            </div>

            {/* How it works — ultra brief */}
            <div style={{ marginTop: '64px', textAlign: 'center' }}>
              <h3 style={{ color: '#E8B44A', fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '1px', marginBottom: '24px' }}>
                How It Works
              </h3>
              <div className="d-flex justify-content-center gap-4 flex-wrap">
                {[
                  { step: '1', title: 'Enter Address', desc: 'Just your address. We already know the details.' },
                  { step: '2', title: 'AI Analyzes', desc: 'ML model + MLS comps + market data in 5 seconds.' },
                  { step: '3', title: 'Chat & Decide', desc: 'Ask AI anything about pricing. List when ready.' }
                ].map((item, i) => (
                  <div key={i} style={{ maxWidth: '200px' }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '50%',
                      background: '#C9952A', color: '#0F1E35', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 12px', fontSize: '18px'
                    }}>
                      {item.step}
                    </div>
                    <div style={{ color: 'white', fontWeight: 600, fontSize: '16px', marginBottom: '4px' }}>{item.title}</div>
                    <div style={{ color: '#8FA3BF', fontSize: '13px' }}>{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom tagline */}
            <div style={{ textAlign: 'center', marginTop: '64px', paddingBottom: '48px' }}>
              <p style={{ color: '#8FA3BF', fontSize: '13px' }}>
                Powered by KairosIQ AI Realty | Designated Broker: Michael Boykin | License CO721005000
              </p>
              <p style={{ color: '#5a6d85', fontSize: '11px' }}>
                No signup required. No obligation. Your data is private.
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HomeValuation;
